import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  TextField,
  Box,
  FormLabel,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { getAuth } from "firebase/auth";
import { CoordinatorActionsService } from "../../services/coordinatorActionsService";

interface ActionSurveyAlert {
  id?: string;
  alertId?: string;
  participantId?: string;
  alertType?: string;
  reason?: string;
  lastSyncDate?: string | null;
  preAlertAdherence7d?: number | null;
  studyId?: string;
}

interface Props {
  open: boolean;
  alert?: ActionSurveyAlert | null;
  onClose: () => void;
  onSave?: (payload: any) => void;
}

const ActionSurveyDialog: React.FC<Props> = ({
  open,
  alert = null,
  onClose,
  onSave,
}) => {
  const [action, setAction] = useState<
    "contact" | "dismissed" | "snoozed" | "resolved" | "follow_up"
  >("contact");
  const [contactMethod, setContactMethod] = useState<
    "email" | "phone" | "sms" | "in_person"
  >("email");
  const [note, setNote] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    // Get token from Firebase auth
    const auth = getAuth();
    auth.currentUser?.getIdToken().then((t) => setToken(t));
  }, []);

  useEffect(() => {
    if (!open) {
      // reset
      setAction("contact");
      setContactMethod("email");
      setNote("");
      setIsLoading(false);
    }
  }, [open]);

  const handleSave = async () => {
    try {
      setIsLoading(true);

      // Ensure we have an alertId from the normalized alert (alert_id from API)
      if (!alert?.alertId) {
        setSnackbar({
          open: true,
          message:
            "Missing alert identifier for this alert — cannot record coordinator action.",
          severity: "error",
        });
        setIsLoading(false);
        return;
      }

      // Prepare payload for backend
      const payload = {
        alertId: alert.alertId,
        actionType: action,
        contactMethod:
          action === "contact"
            ? (contactMethod as "email" | "phone" | "sms" | "in_person")
            : undefined,
        coordinatorNotes: note,
      };

      // Call backend to save coordinator action
      const result = await CoordinatorActionsService.saveAction(token, payload);

      if (result.success) {
        // Show success snackbar
        setSnackbar({
          open: true,
          message: "Action recorded successfully!",
          severity: "success",
        });

        // Call optional onSave callback for any additional UI updates
        if (onSave) onSave(payload);

        // Close dialog after showing success
        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        throw new Error(result.error || "Failed to save action");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save action";

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (d?: string | null) => {
    if (!d) return "—";
    try {
      const parsed = new Date(d);
      if (isNaN(parsed.getTime())) return String(d);
      return parsed.toLocaleDateString();
    } catch (e) {
      return String(d);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, p: 0 } }}
    >
      <DialogTitle sx={{ fontSize: "1rem", pb: 0.5 }}>
        Quick questions
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1 }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Participant
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", fontSize: "0.95rem", mt: 0.25 }}
          >
            {alert?.participantId || "—"}
          </Typography>
        </Box>

        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Alert
          </Typography>
          <Typography variant="body2">
            {alert?.alertType || "—"}
            {alert?.reason ? ` — ${alert.reason}` : ""}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Last sync
            </Typography>
            <Typography variant="body2">
              {formatDate(alert?.lastSyncDate)}
            </Typography>
          </Box>
          <Box sx={{ width: 1 }}>
            <Typography variant="caption" color="text.secondary">
              7d adherence
            </Typography>
            <Typography variant="body2">
              {alert?.preAlertAdherence7d != null
                ? `${Math.round(alert.preAlertAdherence7d)}%`
                : "—"}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 1 }}>
          <FormLabel component="legend" sx={{ mb: 0.5, fontSize: "0.85rem" }}>
            Action
          </FormLabel>
          <RadioGroup
            value={action}
            onChange={(e) =>
              setAction(
                e.target.value as
                  | "contact"
                  | "dismissed"
                  | "snoozed"
                  | "resolved"
                  | "follow_up"
              )
            }
          >
            <FormControlLabel
              value="contact"
              control={<Radio size="small" />}
              label="Contact"
            />
            <FormControlLabel
              value="dismissed"
              control={<Radio size="small" />}
              label="Marked as false positive"
            />
            <FormControlLabel
              value="snoozed"
              control={<Radio size="small" />}
              label="Snoozed"
            />
            <FormControlLabel
              value="resolved"
              control={<Radio size="small" />}
              label="Resolved"
            />
            <FormControlLabel
              value="follow_up"
              control={<Radio size="small" />}
              label="Need follow up"
            />
          </RadioGroup>
        </Box>

        {action === "contact" && (
          <Box sx={{ mt: 1 }}>
            <FormLabel component="legend" sx={{ mb: 0.5, fontSize: "0.85rem" }}>
              Contact method
            </FormLabel>
            <RadioGroup
              value={contactMethod}
              onChange={(e) =>
                setContactMethod(
                  e.target.value as "email" | "phone" | "sms" | "in_person"
                )
              }
              row
            >
              <FormControlLabel
                value="email"
                control={<Radio size="small" />}
                label="Email"
              />
              <FormControlLabel
                value="phone"
                control={<Radio size="small" />}
                label="Phone"
              />
              <FormControlLabel
                value="sms"
                control={<Radio size="small" />}
                label="SMS"
              />
              <FormControlLabel
                value="in_person"
                control={<Radio size="small" />}
                label="In person"
              />
            </RadioGroup>
          </Box>
        )}

        <Box sx={{ mt: 1 }}>
          <TextField
            label="Quick note (optional)"
            variant="filled"
            fullWidth
            size="small"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            InputProps={{
              sx: { bgcolor: "rgba(0,0,0,0.02)", borderRadius: 1 },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button onClick={onClose} size="small" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

// named + default export to help TS/IDE resolve module imports
export { ActionSurveyDialog };
export default ActionSurveyDialog;
